import { Mark } from '@tiptap/core'

type StyledAttrs = {
  title: string | null
  class: string
}

export type StyledOptions = {
  class: {
    default: string
  }
}

/**
 * When rendering a styled mark to CSS, the class will be serialized in this HTML data attribute.
 *  This means that when you copy to the clipboar, the HTML content will contain this data in this attribute.
 *  When you then paste back the HTML code to the rich text editor, the Styled plugin can deserialize the class name.
 */
const dataClassAttribute = 'data-mark-styled-class'

declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Commands<ReturnType> {
    styled: {
      resetStyled: () => ReturnType
      setStyled: (value: Record<string, unknown>) => ReturnType
    }
  }
}

export default Mark.create<StyledOptions>({
  name: 'styled',

  addAttributes() {
    return {
      class: {
        default: '',
        parseHTML(element): string {
          return element.getAttribute(dataClassAttribute) ?? ''
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span',
        // when we return false, this mark will be ignored
        getAttrs: (node: HTMLElement): StyledAttrs | false => {
          const hasExpectedClass =
            node.getAttribute('class') === 'ProseMirror__styled'
          const titleAttribute = node.getAttribute('title')

          if (hasExpectedClass) {
            return {
              class: 'ProseMirror__styled',
              title: titleAttribute,
            }
          }

          return false
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = {
      class: 'ProseMirror__styled',
      title: HTMLAttributes.class,
      [dataClassAttribute]: HTMLAttributes.class,
    }

    return ['span', attrs, 0]
  },

  addCommands() {
    return {
      resetStyled:
        () =>
        ({ commands }): boolean => {
          return commands.unsetMark('styled')
        },

      setStyled:
        (value: Record<string, unknown>) =>
        ({ commands }): boolean => {
          return commands.setMark('styled', value)
        },
    }
  },
})
